import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Copy, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { toast as sonnerToast } from "sonner";

const CreateTestUsers = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [showPasswords, setShowPasswords] = useState<{ [key: number]: boolean }>({});
  const { toast } = useToast();

  const createUsers = async () => {
    setLoading(true);
    setResults([]);
    setShowPasswords({});

    try {
      const { data, error } = await supabase.functions.invoke('create-test-users');

      if (error) throw error;

      setResults(data.results);
      
      const successCount = data.results.filter((r: any) => r.success).length;
      toast({
        title: "Test Users Created",
        description: `Successfully created ${successCount} test users.`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (index: number) => {
    setShowPasswords(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    sonnerToast.success(`${label} copied to clipboard`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Link to="/admin/management">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Management Dashboard
            </Button>
          </Link>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Create Test Users</CardTitle>
            <CardDescription>
              Create 10 test community members for development and testing purposes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={createUsers} disabled={loading} className="w-full">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Users...
                </>
              ) : (
                'Create 10 Test Users'
              )}
            </Button>

            {results.length > 0 && (
              <div className="mt-6 space-y-3">
                <h3 className="font-semibold text-lg">Created Test Users:</h3>
                <div className="space-y-3">
                  {results.map((result, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border-2 ${
                        result.success
                          ? 'bg-green-50 border-green-200 text-green-900 dark:bg-green-900/20 dark:border-green-800 dark:text-green-100'
                          : 'bg-red-50 border-red-200 text-red-900 dark:bg-red-900/20 dark:border-red-800 dark:text-red-100'
                      }`}
                    >
                      <div className="space-y-2">
                        <div className="font-bold text-base">{result.username}</div>
                        {result.success ? (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">Email:</span>
                              <span className="text-sm flex-1">{result.email}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyToClipboard(result.email, "Email")}
                                className="h-7 w-7 p-0"
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">Password:</span>
                              <span className="text-sm flex-1 font-mono">
                                {showPasswords[index] ? result.password : '••••••••'}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => togglePasswordVisibility(index)}
                                className="h-7 w-7 p-0"
                              >
                                {showPasswords[index] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyToClipboard(result.password, "Password")}
                                className="h-7 w-7 p-0"
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          </>
                        ) : (
                          <div className="text-sm font-medium">Error: {result.error}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateTestUsers;
